<template>
    <div class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid">
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
            >
                Two Factor Authentication
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                Two-Factor Authentication (2FA) can be used to help protect your
                account from unauthorized access by requiring you to enter a
                security code when you sign in.
            </p>
        </div>
        <div class="mx-auto w-full rounded-[6px] bg-white shadow-card lg:mx-0">
            <div class="border-b px-6 pb-6">
                <p
                    v-if="!twoFactorEnabled"
                    class="text-md pt-6 leading-normal text-gray-800"
                >
                    Two-Factor Authentication is
                    <span class="font-medium text-topol-red-600"
                        >not enabled</span
                    >
                </p>
                <p v-else class="text-md pt-6 leading-normal text-gray-800">
                    Two-Factor Authentication is
                    <span class="font-medium text-green-600">enabled</span>
                </p>
                <p class="pt-6 text-sm leading-normal text-gray-500">
                    When Two-Factor Authentication (2FA) is enabled, signing in
                    with your password or a social account asks for a second
                    step: a security code from your Authenticator App of choice,
                    or one of your passkeys. You can learn more about 2FA in our
                    documentation.
                </p>

                <div
                    class="mt-6 flex items-center justify-between gap-4 rounded-[6px] border border-gray-200 p-4"
                >
                    <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-800">
                            Use passkeys for 2FA
                        </p>
                        <p class="text-sm text-gray-500">
                            <template v-if="!hasPasskeys"
                                >No passkeys set up. Add a passkey to use this
                                method for 2FA.</template
                            >
                            <template v-else-if="passkeyTwoFactorEnabled"
                                >Sign-ins ask for one of your passkeys.</template
                            >
                            <template v-else
                                >Use one of your passkeys as the second step
                                when you sign in.</template
                            >
                        </p>
                    </div>

                    <component
                        :is="confirmComponent"
                        v-if="hasPasskeys"
                        :allow-passkey="true"
                        :force-confirmation="
                            props.has_password && !passkeyTwoFactorEnabled
                        "
                        @confirmed="togglePasskeyTwoFactor"
                    >
                        <Toggle
                            :model-value="passkeyTwoFactorEnabled"
                            :class="{ 'opacity-25': passkeyLoading }"
                            aria-label="Use passkeys for two-factor authentication"
                        />
                    </component>
                </div>

                <component
                    :is="confirmComponent"
                    v-if="twoFactorEnabled"
                    :allow-passkey="hasPasskeys"
                    @confirmed="showRecoveryCodes = true"
                >
                    <AtomicButton class="mt-6" color="white" size="sm">
                        Show recovery codes
                    </AtomicButton>
                </component>
                <component
                    :is="confirmComponent"
                    v-if="authenticatorEnabled"
                    :allow-passkey="hasPasskeys"
                    @confirmed="showPairingCodes = true"
                >
                    <AtomicButton class="ml-3 mt-6" color="white" size="sm">
                        Pairing QR code
                    </AtomicButton>
                </component>
            </div>

            <div class="flex justify-end">
                <div class="my-3 pr-6">
                    <component
                        :is="confirmComponent"
                        v-if="!authenticatorEnabled"
                        :allow-passkey="hasPasskeys"
                        @confirmed="showTwoFactorAuthentication = true"
                    >
                        <AtomicButton color="yellow" size="sm">
                            Set up authenticator app
                        </AtomicButton>
                    </component>

                    <component
                        :is="confirmComponent"
                        v-else
                        :allow-passkey="hasPasskeys"
                        @confirmed="disableTwoFactorAuthentication"
                    >
                        <AtomicButton
                            color="yellow"
                            size="sm"
                            :class="{ 'opacity-25': loading }"
                            :disabled="loading"
                        >
                            Disable authenticator app
                        </AtomicButton>
                    </component>
                </div>
            </div>
        </div>

        <TwoFactorAuthenticationModal
            v-if="showTwoFactorAuthentication"
            :has_password="props.has_password"
            :has-passkeys="hasPasskeys"
            @close="showTwoFactorAuthentication = false"
            @enabled="onAuthenticatorEnabled"
        />
        <TwoFactorAuthenticationRecoveryCodesModal
            v-if="showRecoveryCodes"
            @close="showRecoveryCodes = false"
        />
        <TwoFactorAuthenticationPairingCodesModal
            v-if="showPairingCodes"
            @close="showPairingCodes = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ConfirmsPassword from './ConfirmsPassword.vue';
import ConfirmsOtp from './ConfirmsOtp.vue';

const globalProps = computed(() => usePage<ITopolPageProps>().props);

const props = defineProps({
    has_password: {
        type: Boolean,
        required: true,
    },
    hasPasskeys: {
        type: Boolean,
        default: false,
    },
});

const confirmComponent = computed(() =>
    props.has_password ? ConfirmsPassword : ConfirmsOtp
);
const loading = ref(false);
const showRecoveryCodes = ref(false);
const showTwoFactorAuthentication = ref(false);
const showPairingCodes = ref(false);
const enabled = ref(false);

const passkeyLoading = ref(false);

const authenticatorEnabled = computed(() => {
    return (
        globalProps.value.user.two_factor_authenticator_enabled || enabled.value
    );
});

const passkeyTwoFactorEnabled = computed(
    () => globalProps.value.user.two_factor_passkey_enabled ?? false,
);

const twoFactorEnabled = computed(() => {
    return authenticatorEnabled.value || passkeyTwoFactorEnabled.value;
});

const onAuthenticatorEnabled = () => {
    enabled.value = true;
    router.reload({ only: ['user'] });
};

// Turning it on needs the typed password (or a passkey confirmed just now), not just a recent confirmation
const togglePasskeyTwoFactor = async (password?: string) => {
    if (passkeyLoading.value) return;

    passkeyLoading.value = true;

    try {
        if (passkeyTwoFactorEnabled.value) {
            await axios.delete(route('auth.passkey-two-factor.destroy'));
        } else {
            const res = await axios.post<{ recovery_codes: string[] | null }>(
                route('auth.passkey-two-factor.store'),
                { password: password || null },
            );

            if (res.data.recovery_codes) {
                showRecoveryCodes.value = true;
            }
        }

        router.reload({ only: ['user'] });
    } catch (error) {
        const errors = axios.isAxiosError(error)
            ? error.response?.data?.errors
            : null;

        create({
            title:
                (errors && (Object.values(errors).flat()[0] as string)) ||
                'Failed to update passkey 2FA',
            type: 'error',
        });
    } finally {
        passkeyLoading.value = false;
    }
};

const disableTwoFactorAuthentication = () => {
    loading.value = true;
    router.delete('/user/two-factor-authentication', {
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            enabled.value = false;
        },
    });
};
</script>
